import { useMemo, useRef, useState } from "react";
import { INTERNAL_ROUTES } from "../../../../constants/routes";
import { getThemeIconLogo, getThemeLogo } from "../../../../helpers";
import { useThemeSwitch } from "../../../../theme";
import { getCurrentSelectedTenant } from "../../../../utils";
import { MyJourneyCallout } from "./MyJourneyCallout";

import S from "../UserNavBar.module.scss";

type NavLogoProps = {
  navigateTo: (route: string, openInNewTab?: boolean) => void;
  thumbLogoUrl?: string;
  addMargin?: boolean;
  /** Render the compact logo. Tenant logos are unaffected. */
  iconOnly?: boolean;
  /** data-testid applied to the logo button. */
  testId?: string;
  showMyJourneyCallout?: boolean;
};

export const NavLogo = ({
  navigateTo,
  thumbLogoUrl,
  addMargin,
  iconOnly = false,
  testId = "nav-logo-button",
  showMyJourneyCallout = false,
}: NavLogoProps) => {
  const logoRef = useRef<HTMLButtonElement>(null);
  const { appliedTheme } = useThemeSwitch();
  const themeLogo = useMemo(
    () => (iconOnly ? getThemeIconLogo(appliedTheme) : getThemeLogo(appliedTheme)),
    [appliedTheme, iconOnly]
  );

  const [hasImageError, setHasImageError] = useState(false);

  const logoSrc = thumbLogoUrl && !hasImageError ? thumbLogoUrl : themeLogo;
  const isThemeIcon = iconOnly && logoSrc === themeLogo;
  const currentTenant = getCurrentSelectedTenant();

  const logoButton = (
    <button
      ref={logoRef}
      type="button"
      aria-label={`Go to ${INTERNAL_ROUTES.Learn.name}`}
      data-testid={testId}
      onClick={() => navigateTo(INTERNAL_ROUTES.Learn.route)}
      style={{
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      <img
        alt={`${currentTenant} Logo`}
        src={logoSrc}
        className="nav-logo"
        style={{
          height: "auto",
          width: isThemeIcon ? "32px" : "100px",
          maxHeight: "40px",
          objectFit: "contain",
          margin: addMargin ? "8px 0" : 0,
        }}
        onError={() => setHasImageError(true)}
      />
    </button>
  );

  if (!showMyJourneyCallout) return logoButton;

  return (
    <span className={S.logoCalloutAnchor}>
      {logoButton}
      <MyJourneyCallout logoRef={logoRef} />
    </span>
  );
};
